import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser } from '../types';
import { authService } from '../services/authService';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextType {
  admin: AdminUser | null;
  status: AuthStatus;
  login: (googleUser: any) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    const stored = localStorage.getItem('fotovibe_admin');
    if (stored) {
      setAdmin(JSON.parse(stored));
      setStatus('authenticated');
    } else {
      setStatus('unauthenticated');
    }
  }, []);

  const login = async (googleUser: any) => {
    try {
      const adminUser = await authService.syncUser(googleUser);
      setAdmin(adminUser);
      setStatus('authenticated');
      localStorage.setItem('fotovibe_admin', JSON.stringify(adminUser));
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const logout = () => {
    setAdmin(null);
    setStatus('unauthenticated');
    localStorage.removeItem('fotovibe_admin');
  };

  return (
    <AuthContext.Provider value={{ admin, status, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
